import React from 'react'
import PropTypes, { arrayOf } from 'prop-types'
import { useIntl } from 'react-intl'

import { formatBlogDate } from '@/utils/date'
import { Layout, Row, Rows, Section } from '@/components/layout'
import BlogArticleListingWide from '@/components/blocks/BlogArticleListingWide'
import BlogArticleListingSmall from '@/components/blocks/BlogArticleListingSmall'
import BlogArticleListingSmallHeader from '@/components/blocks/BlogArticleListingSmallHeader'
import BlogPaginate from '@/components/blocks/BlogPaginate'
import { getPostsForCategory, getPopularPosts, getBlogConfig } from '@/lib/blog/post'
import { getCategoryWithSEO } from '@/lib/blog/category'
import { mapBlogCategory } from '@/utils/seo'
import { SeoBlog } from '@/components/layout/Seo'

import styles from '../index.module.scss'

export default function BlogCategory({ category, posts, pageInfo, popularPosts, seo, categoryPageSize }) {
  const { formatMessage } = useIntl()
  const firstBlogIndex = (pageInfo.currentPage - 1) * categoryPageSize
  const lastBlogIndex = pageInfo.currentPage * categoryPageSize

  const paginatedPosts = posts.slice(firstBlogIndex, lastBlogIndex)

  return (
    <>
      <SeoBlog seoDetails={seo} />

      <Layout.Content>

        <Section>
          <Row center textCenter>
            <h1 style={{ marginBottom: '32px' }}>{category.name}</h1>
          </Row>
          <div className={styles.articleSection}>
            <div>
              <Rows gap={32}>
                {paginatedPosts.map((post, index) => (
                  <BlogArticleListingWide
                    key={post.slug}
                    categories={post.categories?.edges?.map(({ node }) => ({ ...node, link: `/blog/category/${node.slug}` }))}
                    date={formatBlogDate(new Date(post.date))}
                    details={post.excerpt}
                    id={`blog-category-${category}-${index}`}
                    img={post.featuredImage?.node?.sourceUrl}
                    imgAlt={post.featuredImage?.node?.altText}
                    link={`/blog/${post.slug}`}
                    title={post.title}
                  />
                ))}
              </Rows>
            </div>
            <div>
              <Rows gap={0}>
                <BlogArticleListingSmallHeader title={formatMessage({ id: 'pages.blog.popularArticles' })} />
                {popularPosts.map((popularPost, index) => (
                  <BlogArticleListingSmall
                    key={popularPost.slug}
                    date={formatBlogDate(new Date(popularPost.date))}
                    id={`featured-${popularPost.title}-${index}`}
                    img={popularPost.featuredImage?.node?.sourceUrl}
                    imgAlt={popularPost.featuredImage?.node?.altText}
                    link={`/blog/${popularPost.slug}`}
                    title={popularPost.title}
                  />
                ))}
              </Rows>
            </div>
          </div>
        </Section>
        <Section>
          <BlogPaginate currentPage={pageInfo.currentPage} path={pageInfo.path} totalPages={pageInfo.totalPages} />
        </Section>
      </Layout.Content>
    </>
  )
}

export async function getStaticProps(ctx) {
  const [category, , pageNumber = '1'] = ctx.params.category

  const { category: categoryWithSEO } = await getCategoryWithSEO(category)

  const { category: { slug, name, posts: { nodes: posts } } } = await getPostsForCategory(category)
  const {
    popular_posts: popularPostSlugs,
    category_page_size: categoryPageSize,
    revalidate_seconds: revalidateTime,
  } = await getBlogConfig()
  const popularPosts = await getPopularPosts(popularPostSlugs)

  return {
    props: {
      posts,
      pageInfo: { path: `/blog/category/${category}`, currentPage: parseInt(pageNumber, 10), totalPages: Math.ceil(posts.length / categoryPageSize) },
      category: { slug, name, seoHead: categoryWithSEO.seo?.fullHead || null },
      popularPosts,
      seo: mapBlogCategory(categoryWithSEO),
      categoryPageSize,
    },
    revalidate: revalidateTime,
  }
}

export async function getStaticPaths() {
  return {
    paths: [],
    fallback: 'blocking',
  }
}

BlogCategory.propTypes = {
  category: PropTypes.shape({
    slug: PropTypes.string,
    name: PropTypes.string,
    seoHead: PropTypes.string,
  }),
  posts: arrayOf(PropTypes.shape({})),
  pageInfo: PropTypes.shape({
    path: PropTypes.string,
    currentPage: PropTypes.number,
    totalPosts: PropTypes.number,
    totalPages: PropTypes.number,
  }),
  popularPosts: arrayOf(PropTypes.shape({})),
  seo: PropTypes.shape({}),
  categoryPageSize: PropTypes.number.isRequired,
}
